import { useEffect, useMemo, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { Search, Phone, Navigation, X } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { Footer } from '@/components/layout'

export interface StoreFeature {
  type: 'Feature'
  geometry: { type: 'Point'; coordinates: [number, number] }
  properties: {
    tipologia: string
    tipologia_slug: string
    name: string
    phone: string
    address: string
    link: string
    icon: string
    linee: string[]
    orari?: { giorno: string; fascia: string }[]
    promo?: string[]
  }
}

function haversineDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371
  const dLat = ((lat2 - lat1) * Math.PI) / 180
  const dLon = ((lon2 - lon1) * Math.PI) / 180
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return R * c
}

export const typeColorMap: Record<string, string> = {
  'nashi-argan-store': '#B08D62',
  'nashi-salon': '#CFA980',
  'salone-partner': '#8B7B73',
  'by-nashi': '#5C4F4B',
  'salone-total-nashi': '#3B312E',
  'salone-rivenditore': '#6B5F5A',
}

const lineFilterKeys = [
  { key: 'capelli', labelKey: 'storeLocator.lineFilters.capelli' },
  { key: 'viso-e-corpo', labelKey: 'storeLocator.lineFilters.visoCorpo' },
  { key: 'promo', labelKey: 'storeLocator.lineFilters.promo' },
  { key: 'color-and-care', labelKey: 'storeLocator.lineFilters.colorAndCare' },
]

const typeFilterKeys = [
  { key: 'nashi-argan-store', labelKey: 'storeLocator.typeFilters.nashiArganStore' },
  { key: 'by-nashi', labelKey: 'storeLocator.typeFilters.byNashi' },
  { key: 'salone-partner', labelKey: 'storeLocator.typeFilters.salonePartner' },
  { key: 'nashi-salon', labelKey: 'storeLocator.typeFilters.nashiSalon' },
  { key: 'salone-total-nashi', labelKey: 'storeLocator.typeFilters.totalNashiSalon' },
  { key: 'salone-rivenditore', labelKey: 'storeLocator.typeFilters.saloneRivenditore' },
]

function createCustomIcon(tipologiaSlug: string) {
  const color = typeColorMap[tipologiaSlug] || '#B08D62'
  return L.divIcon({
    className: 'custom-div-icon',
    html: `<div style="background-color:${color};width:28px;height:28px;border-radius:50%;border:2px solid #FFFAF3;box-shadow:0 2px 6px rgba(59,49,46,0.35);display:flex;align-items:center;justify-content:center;">
      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#FFFAF3" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg>
    </div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 28],
    popupAnchor: [0, -28],
  })
}

function MapController({ center, zoom }: { center: [number, number]; zoom: number }) {
  const map = useMap()
  useEffect(() => {
    map.setView(center, zoom, { animate: true })
  }, [center, zoom, map])
  return null
}

function FilterCheckbox({
  label,
  checked,
  onChange,
}: {
  label: string
  checked: boolean
  onChange: () => void
}) {
  return (
    <label className="flex items-center gap-2 cursor-pointer group">
      <span
        className={`w-4 h-4 border flex items-center justify-center transition-colors ${
          checked ? 'bg-bruno border-bruno' : 'border-bruno/30 bg-transparent group-hover:border-bruno/60'
        }`}
      >
        {checked && <span className="text-cream text-[10px]">✓</span>}
      </span>
      <input type="checkbox" checked={checked} onChange={onChange} className="sr-only" />
      <span className="text-sm text-bruno-soft group-hover:text-bruno transition-colors">{label}</span>
    </label>
  )
}

export function StoreLocatorPage() {
  const { t, i18n } = useTranslation('common')
  const lang = i18n.language as 'it' | 'en'
  const [stores, setStores] = useState<StoreFeature[]>([])
  const [loading, setLoading] = useState(true)
  const [userLocation, setUserLocation] = useState<[number, number] | null>(null)
  const [search, setSearch] = useState('')
  const [selectedTypes, setSelectedTypes] = useState<string[]>([])
  const [selectedLines, setSelectedLines] = useState<string[]>([])
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [selectedStore, setSelectedStore] = useState<StoreFeature | null>(null)
  const [mapCenter, setMapCenter] = useState<[number, number]>([41.9028, 12.4964]) // Roma default (centro Italia)
  const [mapZoom, setMapZoom] = useState(6)

  useEffect(() => {
    fetch('/data/stores.json')
      .then((r) => r.json())
      .then((data: { features: StoreFeature[] }) => {
        setStores(data.features)
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [])

  useEffect(() => {
    if (!navigator.geolocation) return
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const coords: [number, number] = [pos.coords.latitude, pos.coords.longitude]
        setUserLocation(coords)
        setMapCenter(coords)
        setMapZoom(11)
      },
      () => {}
    )
  }, [])

  const activeFilterCount = selectedTypes.length + selectedLines.length

  const filtered = useMemo(() => {
    let result = stores
    const q = search.trim().toLowerCase()
    if (q) {
      result = result.filter(
        (s) =>
          s.properties.name.toLowerCase().includes(q) ||
          s.properties.address.toLowerCase().includes(q) ||
          s.properties.tipologia.toLowerCase().includes(q)
      )
    }
    if (selectedTypes.length > 0) {
      result = result.filter((s) => selectedTypes.includes(s.properties.tipologia_slug))
    }
    if (selectedLines.length > 0) {
      result = result.filter((s) => s.properties.linee.some((l) => selectedLines.includes(l)))
    }
    if (userLocation) {
      result = result
        .map((s) => {
          const dist = haversineDistance(
            userLocation[0],
            userLocation[1],
            s.geometry.coordinates[1],
            s.geometry.coordinates[0]
          )
          return { feature: s, distance: dist }
        })
        .sort((a, b) => a.distance - b.distance)
        .map((a) => a.feature)
    }
    return result
  }, [stores, search, selectedTypes, selectedLines, userLocation])

  const handleFlyTo = useCallback((store: StoreFeature) => {
    setSelectedStore(store)
    setMapCenter([store.geometry.coordinates[1], store.geometry.coordinates[0]])
    setMapZoom(15)
  }, [])

  const toggleType = (key: string) => {
    setSelectedTypes((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]))
  }

  const toggleLine = (key: string) => {
    setSelectedLines((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]))
  }

  const clearFilters = () => {
    setSelectedTypes([])
    setSelectedLines([])
    setSearch('')
  }

  const listSlice = filtered.slice(0, 80)

  const helpUsText = t('storeLocator.helpUsText', "Non trovi un Salone vicino a te? Clicca qui e aiutaci a raggiungerti!")

  if (loading) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-cream">
        <p className="text-bruno-muted text-sm tracking-wide uppercase">{t('aria.loading', 'Caricamento')}</p>
      </main>
    )
  }

  return (
    <main className="pt-0 min-h-screen bg-cream">
      <div className="nashi-container pb-8 md:pb-12 pt-0">
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="font-display text-bruno mb-8"
          style={{ fontSize: 'clamp(2rem, 4vw, 3.5rem)', lineHeight: 1.05, letterSpacing: '-0.015em' }}
        >
          {t('storeLocator.title', 'Store Locator')}
        </motion.h1>

        <div className="flex flex-col lg:flex-row gap-6 h-[calc(100vh-220px)] min-h-[600px]">
          {/* Sidebar */}
          <div className="w-full lg:w-[380px] flex flex-col gap-4 overflow-hidden">
            <div className="relative shrink-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-bruno-muted" strokeWidth={1.5} />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('storeLocator.searchPlaceholder', 'Cerca città, indirizzo o nome')}
                className="nashi-input w-full pl-10 pr-4 py-3"
              />
            </div>

            {/* Filter toggle */}
            <button
              onClick={() => setFiltersOpen((v) => !v)}
              className="shrink-0 flex items-center justify-between w-full py-3 px-4 bg-bruno text-cream text-[11px] uppercase tracking-[0.16em] hover:bg-argan-deep transition-colors"
            >
              <span>
                {t('storeLocator.filterSearch', 'Filtra la ricerca')}
                {activeFilterCount > 0 && (
                  <span className="ml-2 inline-flex items-center justify-center w-5 h-5 rounded-full bg-cream text-bruno text-[10px] font-medium">
                    {activeFilterCount}
                  </span>
                )}
              </span>
              <span className="text-cream/70">{filtersOpen ? '−' : '+'}</span>
            </button>

            <AnimatePresence>
              {filtersOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden shrink-0"
                >
                  <div className="border border-bruno/10 bg-white/60 p-4 space-y-5">
                    {activeFilterCount > 0 && (
                      <button
                        onClick={clearFilters}
                        className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] text-bruno-muted hover:text-bruno transition-colors"
                      >
                        <X className="w-3 h-3" strokeWidth={1.5} />
                        {t('storeLocator.clearFilters', 'Cancella filtri')}
                      </button>
                    )}

                    <div>
                      <p className="text-[10px] uppercase tracking-[0.24em] text-argan-deep font-medium mb-3">
                        {t('storeLocator.productLine', 'Linea di prodotti')}
                      </p>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                        {lineFilterKeys.map((lf) => (
                          <FilterCheckbox
                            key={lf.key}
                            label={t(lf.labelKey, lf.key)}
                            checked={selectedLines.includes(lf.key)}
                            onChange={() => toggleLine(lf.key)}
                          />
                        ))}
                      </div>
                    </div>

                    <div>
                      <p className="text-[10px] uppercase tracking-[0.24em] text-argan-deep font-medium mb-3">
                        {t('storeLocator.storeType', 'Tipologia di negozio')}
                      </p>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                        {typeFilterKeys.map((tf) => (
                          <FilterCheckbox
                            key={tf.key}
                            label={t(tf.labelKey, tf.key)}
                            checked={selectedTypes.includes(tf.key)}
                            onChange={() => toggleType(tf.key)}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {userLocation && (
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] text-argan-deep shrink-0">
                <Navigation className="w-3 h-3" strokeWidth={1.5} />
                <span>{t('storeLocator.sortedByDistance', 'Ordinati per distanza')}</span>
              </div>
            )}

            <div className="flex-1 overflow-y-auto space-y-3 pr-1">
              {listSlice.map((store) => {
                const dist = userLocation
                  ? haversineDistance(
                      userLocation[0],
                      userLocation[1],
                      store.geometry.coordinates[1],
                      store.geometry.coordinates[0]
                    )
                  : null
                const isActive = selectedStore?.properties.name === store.properties.name
                return (
                  <motion.div
                    key={store.properties.name + store.geometry.coordinates.join(',')}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className={`p-4 border transition-colors cursor-pointer ${
                      isActive
                        ? 'border-argan bg-cream-soft'
                        : 'border-bruno/10 hover:border-bruno/30 bg-white/60'
                    }`}
                    onClick={() => handleFlyTo(store)}
                  >
                    <div className="flex justify-between items-start mb-1">
                      <span className="text-[10px] uppercase tracking-[0.16em] text-argan-deep font-medium">
                        {store.properties.tipologia}
                      </span>
                      {dist !== null && (
                        <span className="text-[11px] text-bruno-muted tabular-nums">{dist.toFixed(1)} km</span>
                      )}
                    </div>
                    <h3 className="font-display text-bruno text-base mb-1">{store.properties.name}</h3>
                    <p className="text-sm text-bruno-soft leading-relaxed mb-3">{store.properties.address}</p>
                    {store.properties.phone && (
                      <a
                        href={`tel:${store.properties.phone}`}
                        className="text-sm text-bruno-muted hover:text-argan-deep flex items-center gap-1.5 mb-3"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Phone className="w-3.5 h-3.5" strokeWidth={1.5} />
                        {store.properties.phone}
                      </a>
                    )}
                    <div className="flex gap-2">
                      <a
                        href={`/${lang}/store-locator/${store.properties.link}`}
                        className="flex-1 text-center text-[10px] uppercase tracking-[0.16em] py-2.5 px-3 border border-bruno text-bruno hover:bg-bruno hover:text-cream transition-colors"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {t('storeLocator.infoAndPromo', 'Info & promo')}
                      </a>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleFlyTo(store)
                        }}
                        className="flex-1 text-center text-[10px] uppercase tracking-[0.16em] py-2.5 px-3 bg-bruno text-cream hover:bg-argan-deep transition-colors"
                      >
                        {t('storeLocator.viewOnMap', 'Vedi sulla mappa')}
                      </button>
                    </div>
                  </motion.div>
                )
              })}
              {filtered.length === 0 && (
                <p className="text-center text-bruno-muted py-8 text-sm">
                  {t('storeLocator.noResults', 'Nessun risultato trovato.')}
                </p>
              )}
              {filtered.length > listSlice.length && (
                <p className="text-center text-[11px] text-bruno-muted py-2">
                  {t('storeLocator.moreResults', `+ {{count}} altri risultati`, { count: filtered.length - listSlice.length })}
                </p>
              )}
            </div>

            <a
              href="https://nashiargan.com/help-us/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-argan-deep underline hover:text-bruno transition-colors shrink-0"
            >
              {helpUsText}
            </a>
          </div>

          {/* Map */}
          <div className="flex-1 rounded-sm overflow-hidden border border-bruno/10 bg-white">
            <MapContainer
              center={mapCenter}
              zoom={mapZoom}
              style={{ height: '100%', width: '100%' }}
              scrollWheelZoom={true}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <MapController center={mapCenter} zoom={mapZoom} />
              {filtered.slice(0, 200).map((store) => (
                <Marker
                  key={store.properties.name + store.geometry.coordinates.join(',')}
                  position={[store.geometry.coordinates[1], store.geometry.coordinates[0]]}
                  icon={createCustomIcon(store.properties.tipologia_slug)}
                  eventHandlers={{
                    click: () => setSelectedStore(store),
                  }}
                >
                  <Popup>
                    <div className="p-1 min-w-[200px]">
                      <p className="text-[10px] uppercase tracking-[0.16em] text-argan-deep font-medium mb-1">
                        {store.properties.tipologia}
                      </p>
                      <p className="font-display text-bruno text-base">{store.properties.name}</p>
                      <p className="text-sm text-bruno-soft mt-1 leading-relaxed">{store.properties.address}</p>
                      {store.properties.phone && (
                        <p className="text-sm mt-2 flex items-center gap-1.5 text-bruno-muted">
                          <Phone className="w-3.5 h-3.5" strokeWidth={1.5} />
                          {store.properties.phone}
                        </p>
                      )}
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>
        </div>
      </div>
      <Footer />
    </main>
  )
}
